import { createFileRoute, useParams } from '@tanstack/react-router'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useBreadcrumbs } from '../../../../../../hooks/useBreadcrumbs'
import { useJobExecution } from '../../../../../../api/jobs'
import { Badge } from '../../../../../../components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '../../../../../../components/ui/card'
import { Separator } from '../../../../../../components/ui/separator'
import { Input } from '../../../../../../components/ui/input'
import { Label } from '../../../../../../components/ui/label'
import { ScrollArea } from '../../../../../../components/ui/scroll-area'
import { AppIcon } from '../../../../../../components/ui/icon'
import {
  mdiClockOutline,
  mdiLoading,
  mdiCheckCircle,
  mdiAlertCircle,
  mdiCancel,
  mdiAlert,
  mdiCalendarClock,
  mdiSpeedometer,
  mdiTextBox,
  mdiMagnify,
  mdiCurrencyUsd,
} from '@mdi/js'
import { PageHeader } from '../../../../../../components/shared/PageHeader'
import { SECTION_ICONS } from '../../../../../../lib/section-icons'
import { EmptyState } from '../../../../../../components/ui/empty-state'

// Mirrors internal/handlers/docprocessing/aicost.Usage / Report (Go) — see
// specs/todos/2026-07-09-02-track-ai-call-costs-in-job-output.md. Stored
// under the reserved "ai_usage" key in execution.output by the job runner.
interface AIUsageCall {
  provider: string
  model: string
  kind: string
  purpose?: string
  input_tokens?: number
  output_tokens?: number
  cached_tokens?: number
  pages?: number
  cost_usd: number
  estimated?: boolean
}

interface AIUsageTotals {
  input_tokens?: number
  output_tokens?: number
  cached_tokens?: number
  pages?: number
  cost_usd: number
}

interface AIUsageReport {
  calls: AIUsageCall[]
  totals: AIUsageTotals
}

function formatCostUSD(cost: number) {
  // Costs are often sub-cent (e.g. a single OCR page ≈ $0.0015) — 4 decimals
  // keeps them visible instead of rounding to "$0.00".
  return `$${cost.toFixed(4)}`
}

export const Route = createFileRoute('/$org/jobs/$jobUid/executions/$executionUid/')({
  component: JobExecutionDetail,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      logs_limit: (search.logs_limit as number) || 100,
    }
  },
})

const statusConfig = {
  pending: { color: 'bg-yellow-100 text-yellow-800', icon: mdiClockOutline },
  running: { color: 'bg-blue-100 text-blue-800', icon: mdiLoading },
  success: { color: 'bg-green-100 text-green-800', icon: mdiCheckCircle },
  failed: { color: 'bg-red-100 text-red-800', icon: mdiAlertCircle },
  cancelled: { color: 'bg-muted text-muted-foreground', icon: mdiCancel },
  lost: { color: 'bg-orange-100 text-orange-800', icon: mdiAlert },
  manual: { color: 'bg-purple-100 text-purple-800', icon: mdiAlert },
}

function JobExecutionDetail() {
  const { org, jobUid, executionUid } = useParams({ from: '/$org/jobs/$jobUid/executions/$executionUid/' })
  const [logSearch, setLogSearch] = useState('')
  const { t } = useTranslation('jobs')

  useBreadcrumbs([
    { title: t('common:nav.home'), path: `/${org}` },
    { title: t('list.title'), path: `/${org}/jobs` },
    { title: `${t('detail.title')} ${jobUid}`, path: `/${org}/jobs/${jobUid}` },
    { title: `${t('execution.title')} ${executionUid}` }
  ])

  const { data: execution, isLoading, error } = useJobExecution(org, jobUid, executionUid, 500)

  const formatDuration = (timeMs?: number) => {
    if (!timeMs) return 'N/A'
    
    if (timeMs < 1000) {
      return `${timeMs}ms`
    } else if (timeMs < 60000) {
      return `${(timeMs / 1000).toFixed(1)}s`
    } else {
      const minutes = Math.floor(timeMs / 60000)
      const seconds = Math.floor((timeMs % 60000) / 1000)
      return `${minutes}m ${seconds}s`
    }
  }

  const filteredLogs = execution?.logs?.filter(log => {
    if (!logSearch) return true
    
    const logContent = JSON.stringify(log.message).toLowerCase()
    return logContent.includes(logSearch.toLowerCase())
  }) || []

  const formatLogContent = (content: any) => {
    if (typeof content === 'object') {
      return JSON.stringify(content, null, 2)
    }
    return String(content)
  }

  if (error) {
    return (
      <div>
        <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
          <h3 className="text-lg font-medium text-destructive">{t('execution.error_loading')}</h3>
          <p className="text-destructive mt-1">{(error as any)?.title || (error as any)?.message || t('common:status.error')}</p>
        </div>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div>
        <div className="flex justify-center items-center py-8">
          <AppIcon path={mdiLoading} spin size="lg" />
          <span className="ml-2">{t('execution.loading')}</span>
        </div>
      </div>
    )
  }

  if (!execution) {
    return (
      <div>
        <div className="text-center py-8">
          <h3 className="text-lg font-medium text-foreground">{t('execution.not_found')}</h3>
          <p className="text-muted-foreground mt-1">{t('execution.not_found_description')}</p>
        </div>
      </div>
    )
  }

  const statusInfo = statusConfig[execution.status as keyof typeof statusConfig] || statusConfig.pending

  const aiUsage = (execution.output as Record<string, unknown> | undefined)?.ai_usage as AIUsageReport | undefined

  return (
    <div>
      <PageHeader
        className="mb-6"
        icon={SECTION_ICONS.jobs}
        title={t('execution.title')}
        description={
          <span className="flex items-center gap-4">
            <Badge className={statusInfo.color}>
              <AppIcon path={statusInfo.icon} size="xs" className="mr-1" />
              {execution.status}
            </Badge>
            <span className="text-sm text-muted-foreground">UID: {execution.uid}</span>
          </span>
        }
      />

      <div className="grid grid-cols-1 gap-6">
        {/* Execution Information */}
        <Card>
          <CardHeader>
            <CardTitle>{t('execution.information')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="flex items-center gap-2">
                <AppIcon path={mdiSpeedometer} size="md" />
                <div>
                  <div className="text-sm text-muted-foreground">{t('execution.status')}</div>
                  <Badge className={statusInfo.color}>
                    <AppIcon path={statusInfo.icon} size="xs" className="mr-1" />
                    {execution.status}
                  </Badge>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <AppIcon path={mdiClockOutline} size="md" />
                <div>
                  <div className="text-sm text-muted-foreground">{t('execution.duration')}</div>
                  <div className="font-medium">{formatDuration(execution.time_spent_ms)}</div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <AppIcon path={mdiCalendarClock} size="md" />
                <div>
                  <div className="text-sm text-muted-foreground">{t('execution.started')}</div>
                  <div className="font-medium">{new Date(execution.created_at).toLocaleString()}</div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <AppIcon path={mdiCalendarClock} size="md" />
                <div>
                  <div className="text-sm text-muted-foreground">{t('execution.updated')}</div>
                  <div className="font-medium">{new Date(execution.updated_at).toLocaleString()}</div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Output */}
        {execution.output && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AppIcon path={mdiTextBox} size="md" />
                {t('execution.output')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-64">
                <pre className="bg-muted/50 p-4 rounded text-sm overflow-x-auto text-foreground">
                  {JSON.stringify(execution.output, null, 2)}
                </pre>
              </ScrollArea>
            </CardContent>
          </Card>
        )}

        {/* AI Usage */}
        {aiUsage && aiUsage.calls && aiUsage.calls.length > 0 && (
          <Card data-testid="execution-ai-usage">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AppIcon path={mdiCurrencyUsd} size="md" />
                {t('ai_usage.title')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div>
                  <div className="text-sm text-muted-foreground">{t('ai_usage.total_cost')}</div>
                  <div className="font-medium" data-testid="ai-usage-total-cost">{formatCostUSD(aiUsage.totals.cost_usd)}</div>
                </div>
                {!!aiUsage.totals.input_tokens && (
                  <div>
                    <div className="text-sm text-muted-foreground">{t('ai_usage.input_tokens')}</div>
                    <div className="font-medium">{aiUsage.totals.input_tokens.toLocaleString()}</div>
                  </div>
                )}
                {!!aiUsage.totals.output_tokens && (
                  <div>
                    <div className="text-sm text-muted-foreground">{t('ai_usage.output_tokens')}</div>
                    <div className="font-medium">{aiUsage.totals.output_tokens.toLocaleString()}</div>
                  </div>
                )}
                {!!aiUsage.totals.cached_tokens && (
                  <div>
                    <div className="text-sm text-muted-foreground">{t('ai_usage.cached_tokens')}</div>
                    <div className="font-medium">{aiUsage.totals.cached_tokens.toLocaleString()}</div>
                  </div>
                )}
                {!!aiUsage.totals.pages && (
                  <div>
                    <div className="text-sm text-muted-foreground">{t('ai_usage.pages')}</div>
                    <div className="font-medium">{aiUsage.totals.pages.toLocaleString()}</div>
                  </div>
                )}
              </div>

              <Separator />

              <div>
                <div className="text-sm text-muted-foreground mb-2">
                  {t('ai_usage.calls_title', { count: aiUsage.calls.length })}
                </div>
                <div className="space-y-2">
                  {aiUsage.calls.map((call, index) => (
                    <div key={index} className="border rounded p-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                      <Badge variant="outline">{call.kind}</Badge>
                      <span className="text-muted-foreground">{call.model}</span>
                      {call.purpose && <span className="text-muted-foreground">{call.purpose}</span>}
                      {!!call.input_tokens && <span>{t('ai_usage.input_tokens')}: {call.input_tokens.toLocaleString()}</span>}
                      {!!call.output_tokens && <span>{t('ai_usage.output_tokens')}: {call.output_tokens.toLocaleString()}</span>}
                      {!!call.pages && <span>{t('ai_usage.pages')}: {call.pages.toLocaleString()}</span>}
                      <span className="font-medium ml-auto">
                        {formatCostUSD(call.cost_usd)}
                        {call.estimated && <span className="text-muted-foreground"> ({t('ai_usage.estimated')})</span>}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Error */}
        {execution.error && execution.status === 'failed' && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AppIcon path={mdiAlertCircle} size="md" className="text-destructive" />
                {t('execution.error')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-64">
                <pre className="bg-destructive/10 p-4 rounded text-sm overflow-x-auto whitespace-pre-wrap break-words text-destructive border border-destructive/20">
                  {JSON.stringify(execution.error, null, 2)}
                </pre>
              </ScrollArea>
            </CardContent>
          </Card>
        )}

        {/* Logs */}
        {execution.logs && execution.logs.length > 0 && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <AppIcon path={mdiTextBox} size="md" />
                  {t('execution.logs_title', { count: execution.logs.length })}
                </CardTitle>
                <div className="flex items-center gap-2">
                  <Label htmlFor="log-search" className="text-sm">{t('execution.logs_search')}</Label>
                  <div className="relative">
                    <Input
                      id="log-search"
                      placeholder={t('execution.logs_placeholder')}
                      value={logSearch}
                      onChange={(e) => setLogSearch(e.target.value)}
                      className="w-64 pl-8"
                    />
                    <AppIcon tone="muted" path={mdiMagnify} size="xs" className="absolute left-2 top-1/2 transform -translate-y-1/2" />
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-96">
                <div className="space-y-2">
                  {filteredLogs.length === 0 ? (
                    <EmptyState
                      variant="inline"
                      live
                      className="py-4"
                      title={logSearch ? t('execution.logs_empty_search') : t('execution.logs_empty')}
                    />
                  ) : (
                    filteredLogs.map((log, index) => (
                      <div key={index} className="border rounded p-3 bg-muted/30">
                        <div className="flex justify-between items-start mb-2">
                          <span className="text-xs text-muted-foreground font-mono">
                            {new Date(log.time).toLocaleString()}
                          </span>
                          {(() => {
                            const attrs = log.attrs as Record<string, unknown> | undefined
                            const event = attrs?.event
                            return event ? (
                              <Badge variant="outline" className="text-xs">
                                {String(event)}
                              </Badge>
                            ) : null
                          })()}
                        </div>
                        <pre className="text-sm whitespace-pre-wrap overflow-x-auto">
                          {formatLogContent(log.message)}
                        </pre>
                      </div>
                    ))
                  )}
                </div>
              </ScrollArea>
              
              {filteredLogs.length < (execution.logs?.length || 0) && (
                <Separator className="my-4" />
              )}
              
              <div className="text-sm text-muted-foreground text-center">
                {t('execution.logs_showing', { shown: filteredLogs.length, total: execution.logs.length })}
                {execution.logs.length >= 500 && (
                  <span className="block mt-1 text-amber-600">
                    ⚠️ {t('execution.logs_limit_warning')}
                  </span>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* No logs message */}
        {(!execution.logs || execution.logs.length === 0) && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AppIcon path={mdiTextBox} size="md" />
                {t('execution.logs_title', { count: 0 })}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <EmptyState variant="inline" title={t('execution.logs_empty')} />
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  )
}